import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Headers,
  LoadingSpinner,
  Spacer,
  Modal,
} from '@tw/tw-component-library';
import {
  PageContainer,
  ContentSectionContainer,
  ContentSection,
  SectionTitle,
  CardGroup,
  Icon,
  Description,
  Count,
  PersonaDetailsModalContent,
  PersonaDetailsSection,
  PersonaDetailsTitle,
  PersonaDetailsValue,
  PersonaDetailsTags,
  PersonaDetailsTag,
  PersonaDetailsList,
  PersonaDetailsListItem,
} from './PromptLibrary.styles';
import { WorkspaceHeaderLayout } from '@/components/layout/WorkspaceHeaderLayout';
import { getWorkspace } from '@/apis/core/workspace/get/getWorkspace';
import { UseAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import {
  TransformedUserWorkspace,
  transformAllUserWorkspaces,
} from '@/utils/data/transformWorkSpaceItems';
import { useParams } from 'react-router-dom';
import { PlaceholderText } from '../workspacePage/WorkSpacePage.styles';
import { getAskPersonas } from '@/apis/ask/askPersona/get/getAskPersonas';
import { AskPersona } from '@/apis/ask/askPersona/types';
import { PromptLibraryVisualPromptsSection } from './PromptLibraryVisualPromptsSection';

// Mock data for Bots
//TODO: Will leave these in the code for now but will remove them later.
// const mockBots = [
//   {
//     id: '1',
//     name: 'FAQ Bot',
//     description: 'Answers frequently asked questions automatically',
//     chats: ['FAQ Chat #1', 'Help Center Bot', 'Quick Answers'],
//   },
//   {
//     id: '2',
//     name: 'Document Assistant',
//     description: 'Helps with document analysis and summarization',
//     chats: ['Document Review', 'Contract Analysis', 'Report Summary'],
//   },
//   {
//     id: '3',
//     name: 'Code Review Bot',
//     description: 'Assists with code reviews and technical discussions',
//     chats: ['PR Review #456', 'Code Discussion', 'Technical Q&A'],
//   },
//   {
//     id: '4',
//     name: 'Meeting Scheduler',
//     description: 'Handles meeting scheduling and calendar management',
//     chats: ['Schedule Meeting', 'Calendar Sync', 'Appointment Booking'],
//   },
// ];

export const PromptLibrary: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setCurrentWorkspaceId } = useWorkspace();
  const { getAccessToken } = UseAuth();
  const [workspaceData, setWorkspaceData] =
    useState<TransformedUserWorkspace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [personas, setPersonas] = useState<AskPersona[]>([]);
  const [isLoadingPersonas, setIsLoadingPersonas] = useState(false);
  const [personaError, setPersonaError] = useState<Error | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<AskPersona | null>(null);
  const [showPersonaModal, setShowPersonaModal] = useState(false);

  const getWorkspaceData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getWorkspace({
        getAccessToken: getAccessToken,
        workspaceId: id || '',
      });

      const transformed = await transformAllUserWorkspaces(
        [data],
        getAccessToken
      );
      setWorkspaceData({ ...transformed[0] });
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load workspace data');
      setError(error);
      console.error('Failed to load workspace data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id, getAccessToken]);

  useEffect(() => {
    setCurrentWorkspaceId(id || null);
  }, [id, setCurrentWorkspaceId]);

  const fetchPersonas = useCallback(async () => {
    try {
      setIsLoadingPersonas(true);
      setPersonaError(null);
      const fetchedPersonas = await getAskPersonas({ getAccessToken });
      setPersonas(fetchedPersonas);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load personas');
      setPersonaError(error);
      console.error('Failed to load personas:', error);
    } finally {
      setIsLoadingPersonas(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    getWorkspaceData();
    fetchPersonas();
  }, [getWorkspaceData, fetchPersonas]);

  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  if (error) {
    return (
      <PageContainer>
        <ContentSectionContainer>
          <ContentSection>
            <PlaceholderText>
              Error loading workspace: {error.message}
            </PlaceholderText>
          </ContentSection>
        </ContentSectionContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {!isLoading && !error && workspaceData && (
        <>
          <WorkspaceHeaderLayout
            workspaceData={workspaceData}
            activePage="prompt-library"
          />
          <ContentSectionContainer>
            <ContentSection>
              <SectionTitle>
                <Headers headerType="h2">Personas</Headers>
              </SectionTitle>
              <Spacer size={2} />
              {isLoadingPersonas ? (
                <LoadingSpinner variant="fullPage" />
              ) : personaError ? (
                <PlaceholderText>
                  Error loading personas: {personaError.message}
                </PlaceholderText>
              ) : personas.length === 0 ? (
                <PlaceholderText>No personas available</PlaceholderText>
              ) : (
                <CardGroup>
                  {personas.map((persona) => (
                    <Card
                      key={persona.id}
                      hover
                      onClick={() => {
                        setSelectedPersona(persona);
                        setShowPersonaModal(true);
                      }}
                    >
                      <Icon>🎭</Icon>
                      <Headers headerType="h3">{persona.name}</Headers>
                      <Spacer />
                      <Description>
                        {persona.description || 'No description available'}
                      </Description>
                      {persona.tags && persona.tags.length > 0 && (
                        <>
                          <Spacer />
                          <Count>{persona.tags.length} tags</Count>
                        </>
                      )}
                    </Card>
                  ))}
                </CardGroup>
              )}

              <Spacer size={4} />

              {id && (
                <PromptLibraryVisualPromptsSection
                  workspaceId={id}
                  workspaceTitle={workspaceData?.title}
                  getAccessToken={getAccessToken}
                />
              )}

              <Spacer size={4} />

              {/* <SectionTitle>
                <Headers headerType="h2">Bots</Headers>
              </SectionTitle>
              <Spacer size={2} />
              <CardGroup>
                {mockBots.map((bot) => (
                  <Card key={bot.id} hover>
                    <Icon>🤖</Icon>
                    <Headers headerType="h3">{bot.name}</Headers>
                    <Spacer />
                    <Description>{bot.description}</Description>
                    <Spacer />
                    <Count>{bot.chats.length} chats</Count>
                  </Card>
                ))}
              </CardGroup> */}
            </ContentSection>
          </ContentSectionContainer>

          {/* Persona Details Modal */}
          <Modal
            isOpen={showPersonaModal}
            onClose={() => {
              setShowPersonaModal(false);
              setSelectedPersona(null);
            }}
            title={selectedPersona?.name || 'Persona Details'}
            size="large"
          >
            {selectedPersona && (
              <PersonaDetailsModalContent>
                <PersonaDetailsSection>
                  <PersonaDetailsTitle>ID</PersonaDetailsTitle>
                  <PersonaDetailsValue>{selectedPersona.id}</PersonaDetailsValue>
                </PersonaDetailsSection>

                {selectedPersona._etag && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>ETag</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona._etag}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.askPersonaType && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Persona Type</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.askPersonaType}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.description && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Description</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.description}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.version !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Version</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.version}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.dated && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Dated</PersonaDetailsTitle>
                    <PersonaDetailsValue>
                      {new Date(selectedPersona.dated).toLocaleDateString()}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.endDate && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>End Date</PersonaDetailsTitle>
                    <PersonaDetailsValue>
                      {new Date(selectedPersona.endDate).toLocaleDateString()}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.defaultAskTitle && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Default Ask Title</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.defaultAskTitle}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.departments && selectedPersona.departments.length > 0 && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Departments</PersonaDetailsTitle>
                    <PersonaDetailsList>
                      {selectedPersona.departments.map((dept, index) => (
                        <PersonaDetailsListItem key={index}>{dept}</PersonaDetailsListItem>
                      ))}
                    </PersonaDetailsList>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.defaultIcon !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Default Icon</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.defaultIcon}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.initialBotMessage && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Initial Bot Message</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.initialBotMessage}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.systemPrompt && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>System Prompt</PersonaDetailsTitle>
                    <PersonaDetailsValue style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedPersona.systemPrompt}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.systemResponse && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>System Response</PersonaDetailsTitle>
                    <PersonaDetailsValue style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedPersona.systemResponse}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.tags && selectedPersona.tags.length > 0 && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Tags</PersonaDetailsTitle>
                    <PersonaDetailsTags>
                      {selectedPersona.tags.map((tag, index) => (
                        <PersonaDetailsTag key={index}>{tag}</PersonaDetailsTag>
                      ))}
                    </PersonaDetailsTags>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.modelKey && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Model Key</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.modelKey}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.functionsAllowed && selectedPersona.functionsAllowed.length > 0 && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Functions Allowed</PersonaDetailsTitle>
                    <PersonaDetailsList>
                      {selectedPersona.functionsAllowed.map((func, index) => (
                        <PersonaDetailsListItem key={index}>{func}</PersonaDetailsListItem>
                      ))}
                    </PersonaDetailsList>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.completionTokenLimit !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Completion Token Limit</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.completionTokenLimit.toLocaleString()}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.requestTokenLimit !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Request Token Limit</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.requestTokenLimit.toLocaleString()}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.responseTokenLimit !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Response Token Limit</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.responseTokenLimit.toLocaleString()}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.temperature !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Temperature</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.temperature}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.frequencyPenalty !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Frequency Penalty</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.frequencyPenalty}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.interactionLimiter !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Interaction Limiter</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.interactionLimiter}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.invocationPrompt && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Invocation Prompt</PersonaDetailsTitle>
                    <PersonaDetailsValue style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedPersona.invocationPrompt}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.inputSteps && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Input Steps</PersonaDetailsTitle>
                    <PersonaDetailsValue>
                      {JSON.stringify(selectedPersona.inputSteps, null, 2)}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {/* Legacy fields */}
                {selectedPersona.department && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Department (Legacy)</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.department}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.isDefault !== undefined && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Is Default (Legacy)</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.isDefault ? 'Yes' : 'No'}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.userId && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>User ID (Legacy)</PersonaDetailsTitle>
                    <PersonaDetailsValue>{selectedPersona.userId}</PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.createdAt && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Created At (Legacy)</PersonaDetailsTitle>
                    <PersonaDetailsValue>
                      {new Date(selectedPersona.createdAt).toLocaleString()}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}

                {selectedPersona.updatedAt && (
                  <PersonaDetailsSection>
                    <PersonaDetailsTitle>Updated At (Legacy)</PersonaDetailsTitle>
                    <PersonaDetailsValue>
                      {new Date(selectedPersona.updatedAt).toLocaleString()}
                    </PersonaDetailsValue>
                  </PersonaDetailsSection>
                )}
              </PersonaDetailsModalContent>
            )}
          </Modal>
        </>
      )}
    </PageContainer>
  );
};
